import { useCallback, useEffect, useRef, useState } from "react";
import GradientBlue from "../../../Components/InputFields/GradientBlue";
import LogoWhite10 from "../../../Media/PNG/LogoWhite10.svg";
import LogoBlue from "../../../Media/PNG/LogoBlue.svg";
import Back from "./Back";

export default function ContainerForgotPWCode({
  email,
  onBack,
  onContinue,
  onResend,
  onInputChange,
  loading,
  error,
}) {
  const logoSrc = document.documentElement.classList.contains("dark")
    ? LogoWhite10
    : LogoBlue;
  const [inputNums, setInputNums] = useState(["", "", "", "", "", ""]);
  const [codeInvalid, setCodeInvalid] = useState(false);
  const inputRefs = useRef([]);
  const submitInFlightRef = useRef(false);
  const lastAutoSubmittedCodeRef = useRef("");
  const [submitting, setSubmitting] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(60);
  const [resendError, setResendError] = useState("");

  useEffect(() => {
    if (secondsLeft <= 0) return undefined;
    const timer = window.setTimeout(() => setSecondsLeft((value) => value - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [secondsLeft]);

  const handleResend = async () => {
    if (secondsLeft > 0 || loading) return;
    setResendError("");
    try {
      await onResend();
      setInputNums(["", "", "", "", "", ""]);
      setSecondsLeft(60);
    } catch (requestError) {
      setResendError(requestError.message);
    }
  };

  const handlePaste = (event, startIndex) => {
    event.preventDefault();
    const digits = (event.clipboardData?.getData("text") ?? "")
      .replace(/[^0-9]/g, "")
      .slice(0, inputNums.length - startIndex);

    if (!digits) {
      return;
    }

    const updated = [...inputNums];
    digits.split("").forEach((digit, offset) => {
      updated[startIndex + offset] = digit;
    });
    setInputNums(updated);
    setCodeInvalid(false);
    setResendError("");
    lastAutoSubmittedCodeRef.current = "";
    onInputChange?.();
    inputRefs.current[Math.min(startIndex + digits.length, 5)]?.focus();
  };

  const handleDigitChange = (event, index) => {
    const digit = event.target.value.replace(/[^0-9]/g, "").slice(-1);
    const updated = [...inputNums];
    updated[index] = digit;
    setInputNums(updated);
    setCodeInvalid(false);
    setResendError("");
    lastAutoSubmittedCodeRef.current = "";
    onInputChange?.();
    if (digit) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const submitCode = useCallback(async () => {
    if (submitInFlightRef.current || loading) return;
    const verificationCode = inputNums.join("");
    if (verificationCode.length !== 6) {
      setCodeInvalid(true);
      return;
    }
    submitInFlightRef.current = true;
    setSubmitting(true);
    try {
      await onContinue(verificationCode);
    } finally {
      submitInFlightRef.current = false;
      setSubmitting(false);
    }
  }, [inputNums, loading, onContinue]);

  useEffect(() => {
    const code = inputNums.join("");
    if (
      code.length === inputNums.length &&
      code !== lastAutoSubmittedCodeRef.current
    ) {
      lastAutoSubmittedCodeRef.current = code;
      submitCode();
    }
  }, [inputNums, submitCode]);

  const handleSubmit = (event) => {
    event.preventDefault();
    submitCode();
  };

  const isSubmitting = loading || submitting;

  return (
    <div className="w-full max-w-md bg-zi-bg2-light dark:bg-zi-bg2-dark rounded-2xl p-5 m-4 gap-7 flex flex-col border border-zi-blue-light">
      <div className="flex flex-col gap-2 justify-center items-center">
        <img src={logoSrc} alt="لوگو" className="w-14" />
        <p className="font-medium text-sm text-zi-text-light dark:text-zi-text-dark">
          کوژیتو فاند
        </p>
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center">
          <h1 className="text-lg font-medium text-zi-text-light dark:text-zi-text-dark">
            کد یک‌بار مصرف را وارد کنید
          </h1>
          <Back onClick={onBack} />
        </div>
        <span className="text-xs font-light text-zi-text-light/50 dark:text-zi-text-dark/50">
          کدی که به ایمیل {email} ارسال شده را وارد کنید.
        </span>
      </div>
      <form className="flex flex-col gap-7" onSubmit={handleSubmit}>
        <div className="flex flex-col gap-4 items-end">
          <div className="flex justify-between items-center gap-4 [direction:ltr]">
            {inputNums.map((digit, index) => (
              <input
                key={index}
                ref={(element) => {
                  inputRefs.current[index] = element;
                }}
                type="text"
                value={digit}
                maxLength="1"
                aria-label={`رقم ${index + 1} کد یک‌بار مصرف`}
                inputMode="numeric"
                pattern="[0-9]*"
                className="size-full aspect-square rounded-lg caret-zi-blue-light border border-zi-border-light dark:border-zi-border-dark bg-zi-bg2-light dark:bg-zi-bg2-dark hocus:bg-zi-bg1-light hocus:dark:bg-zi-bg1-dark text-center text-xl font-medium text-zi-text-light dark:text-zi-text-dark outline-none hocus:ring-1 hocus:ring-zi-border-light hocus:dark:ring-zi-border-dark [direction:ltr]"
                onChange={(event) => handleDigitChange(event, index)}
                onPaste={(event) => handlePaste(event, index)}
                onKeyDown={(event) => {
                  if (event.key === "Backspace" && !digit) {
                    inputRefs.current[index - 1]?.focus();
                  }
                  if (event.key === "ArrowLeft") {
                    event.preventDefault();
                    inputRefs.current[index - 1]?.focus();
                  }
                  if (event.key === "ArrowRight") {
                    event.preventDefault();
                    inputRefs.current[index + 1]?.focus();
                  }
                }}
              />
            ))}
          </div>
          <button
            type="button"
            disabled={secondsLeft > 0 || isSubmitting}
            onClick={handleResend}
            className="text-zi-blue-light text-xs hocus:underline flex items-center gap-1 disabled:opacity-50 disabled:no-underline"
          >
            <span>ارسال مجدد کد</span>
            {secondsLeft > 0 && <span>(<span>{secondsLeft}</span> ثانیه)</span>}
          </button>
        </div>
        {codeInvalid && (
          <p className="text-red-500 text-sm">لطفا کد شش رقمی را وارد کنید</p>
        )}
        {(error || resendError) && (
          <p className="text-red-500 text-sm">{error || resendError}</p>
        )}
        <GradientBlue
          type="submit"
          value="ثبت"
          isLoader={isSubmitting}
          disabled={isSubmitting}
        />
      </form>
    </div>
  );
}
